import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, Loader2, Eye, QrCode, Check, DollarSign } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import api, { apiError } from "../lib/api";

export default function MailerPage() {
  const { resource, id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  const [mailerTypes, setMailerTypes] = useState(["letter"]);
  const [templates, setTemplates] = useState([]);
  const [letterTplId, setLetterTplId] = useState("builtin-letter");
  const [pcTplId, setPcTplId] = useState("builtin-postcard");
  const toggleType = (t) => setMailerTypes((prev) =>
    prev.includes(t) ? (prev.length === 1 ? prev : prev.filter((x) => x !== t)) : [...prev, t]);
  const [mailerLive, setMailerLive] = useState(false);
  const [mailerBusy, setMailerBusy] = useState(false);
  const [mailerResult, setMailerResult] = useState(null);
  const [mailings, setMailings] = useState([]);
  const [mailingsBusy, setMailingsBusy] = useState(false);
  const [spend, setSpend] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [sellerQr, setSellerQr] = useState(null);
  const portalBase = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    document.title = "ARQITEK — Mailer";
    api.get(`/${resource}/${id}`).then((r) => setRecord(r.data)).catch(() => setRecord(null)).finally(() => setLoading(false));
    api.get(`/sell-land/qr`, { params: { base: portalBase, lead: id, resource } }).then((r) => setSellerQr(r.data)).catch(() => {});
    api.get(`/mailer-templates`).then((r) => {
      const its = r.data.items || []; setTemplates(its);
      const ld = its.find((t) => t.type === "letter" && t.is_default); if (ld) setLetterTplId(ld.id);
      const pd = its.find((t) => t.type === "postcard" && t.is_default); if (pd) setPcTplId(pd.id);
    }).catch(() => {});
    loadMailings();
    loadSpend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, id]);

  const loadMailings = async (refresh = false) => {
    setMailingsBusy(true);
    try {
      const { data } = await api.get(`/postgrid/lead/${id}/mailings?resource=${resource}${refresh ? "&refresh=1" : ""}`);
      setMailings(data.items || []);
    } catch (e) { /* silent */ } finally { setMailingsBusy(false); }
  };

  const loadSpend = async () => {
    try { const { data } = await api.get(`/postgrid/spend`); setSpend(data); } catch (e) { /* silent */ }
  };

  const previewMailer = async () => {
    setPreviewBusy(true);
    try {
      const results = [];
      for (const t of mailerTypes) {
        const { data } = await api.post(`/postgrid/lead/${id}/preview?resource=${resource}`, { type: t, portal_base: portalBase, template_id: t === "letter" ? letterTplId : pcTplId });
        if (data.ok) results.push(data);
      }
      if (results.length) { setPreviewData(results); setPreviewOpen(true); }
      else toast.error("Could not render preview.");
    } catch (e) { toast.error(apiError(e)); } finally { setPreviewBusy(false); }
  };

  const sendMailer = async () => {
    setMailerBusy(true); setMailerResult(null);
    try {
      const results = [];
      for (const t of mailerTypes) {
        const { data } = await api.post(`/postgrid/lead/${id}/send?resource=${resource}`, { type: t, live: mailerLive, verified: true, force_send: true, portal_base: portalBase, template_id: t === "letter" ? letterTplId : pcTplId });
        results.push({ type: t, ...data });
      }
      setMailerResult(results);
      const okCount = results.filter((r) => r.ok).length;
      if (okCount) { toast.success(`${okCount} mailer${okCount > 1 ? "s" : ""} created (${mailerLive ? "LIVE" : "test"}).`); loadMailings(true); loadSpend(); }
      const failed = results.filter((r) => !r.ok);
      if (failed.length) toast.error(failed[0].error || "Some mailers could not be sent.");
    } catch (e) { toast.error(apiError(e)); } finally { setMailerBusy(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="mailer-page-loading">
        <Loader2 className="h-6 w-6 text-primary animate-spin" />
      </div>
    );
  }
  if (!record) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="mailer-page-not-found">
        <h1 className="text-xl font-semibold">Unable to load this record</h1>
        <button onClick={() => navigate(-1)} className="mt-4 text-sm text-primary hover:underline">Go back</button>
      </div>
    );
  }

  const title = record.address || record.street_address || record.property_name || "Lead";
  const ownerName = `${record.first_name || record.owner_first || ""} ${record.last_name || record.owner_last || ""}`.trim() || record.owner_name || "Current Owner";
  const cityLine = [record.mailing_city, [String(record.mailing_state || "").slice(0, 2).toUpperCase(), record.mailing_zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const recipientAddr = record.mailing_address
    ? [record.mailing_address, cityLine].filter(Boolean).join(", ")
    : (record.owner_mailing_address || record.gis_owner_mailing_address || "");

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="mailer-page">
      <div>
        <button
          onClick={() => navigate(`/record/${resource}/${id}`)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
          data-testid="mailer-page-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back to record
        </button>
        <span className="block text-xs uppercase tracking-[0.15em] text-primary mb-2">Direct Mail</span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="mailer-page-title">Send a Mailer</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Arqitek-branded direct mail to {ownerName === "Current Owner" ? "this owner" : ownerName} via PostGrid — for {title}. Test mode won't print or charge.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <div className="rounded-lg border border-border p-3 text-sm space-y-0.5" data-testid="mailer-recipient">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Recipient</div>
              <div className="font-medium" data-testid="mailer-recipient-name">{ownerName}</div>
              {recipientAddr
                ? <div className="text-muted-foreground" data-testid="mailer-recipient-address">{recipientAddr}</div>
                : <div className="text-amber-400 text-xs" data-testid="mailer-no-owner-address">No owner mailing address on file. Add Mailing Address, City, State and ZIP in Owner Information before sending.</div>}
            </div>

            <div>
              <div className="text-xs text-muted-foreground mb-1.5">Choose one or both — they'll be sent together.</div>
              <div className="flex gap-2" data-testid="mailer-type">
                {["letter", "postcard"].map((t) => {
                  const active = mailerTypes.includes(t);
                  return (
                    <button key={t} type="button" onClick={() => toggleType(t)}
                      className={`flex-1 rounded-lg border px-3 py-2.5 text-sm capitalize transition-colors flex items-center justify-center gap-2 ${active ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}
                      data-testid={`mailer-type-${t}`}>
                      <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-[3px] border ${active ? "bg-primary border-primary text-white" : "border-muted-foreground"}`}>{active && <Check className="h-2.5 w-2.5" />}</span>
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            {mailerTypes.map((t) => (
              <div key={t} data-testid={`mailer-tpl-select-${t}`}>
                <label className="text-xs text-muted-foreground capitalize">{t} design</label>
                <select
                  value={t === "letter" ? letterTplId : pcTplId}
                  onChange={(e) => (t === "letter" ? setLetterTplId(e.target.value) : setPcTplId(e.target.value))}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  data-testid={`mailer-tpl-${t}`}
                >
                  {templates.filter((x) => x.type === t).map((x) => (
                    <option key={x.id} value={x.id}>{x.name}{x.is_default ? " (default)" : ""}</option>
                  ))}
                </select>
              </div>
            ))}

            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" checked={mailerLive} onChange={(e) => setMailerLive(e.target.checked)} data-testid="mailer-live" />
              Send LIVE (actually prints &amp; mails). Leave off to test.
            </label>

            {Array.isArray(mailerResult) && mailerResult.some((r) => r.ok) && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm space-y-1" data-testid="mailer-result">
                {mailerResult.filter((r) => r.ok).map((r, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="font-medium text-primary capitalize">{r.type} — {r.status || "ready"} ({r.live ? "LIVE" : "test"})</span>
                    {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-primary underline text-xs" data-testid="mailer-preview-link">PDF</a>}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={previewMailer} disabled={previewBusy} data-testid="mailer-preview-btn">
                {previewBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                Preview
              </Button>
              <Button onClick={sendMailer} disabled={mailerBusy || !recipientAddr} data-testid="mailer-send">
                {mailerBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                {mailerLive ? "Send LIVE" : "Send test"}{mailerTypes.length > 1 ? " (both)" : ""}
              </Button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1 space-y-5">
          <div className="rounded-xl border border-border bg-card p-5" data-testid="mailer-seller-qr">
            <div className="flex items-center gap-2 mb-3">
              <QrCode className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">Seller QR on this mailer</h3>
            </div>
            {sellerQr?.qr ? (
              <img src={sellerQr.qr} alt="Sell Your Land QR" className="w-32 h-32 rounded-lg border border-border bg-white p-1.5" data-testid="mailer-seller-qr-img" />
            ) : (
              <div className="w-32 h-32 rounded-lg border border-border flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
            )}
            <p className="text-xs text-muted-foreground mt-3">This QR is printed on the letter/postcard. When {record.owner_name || "the owner"} scans it, their asking price flows straight back to this lead.</p>
          </div>

          <div className="rounded-xl border border-border bg-card p-5" data-testid="mailer-spend">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">Mail spend</h3>
            </div>
            {spend ? (
              <div className="space-y-3">
                <div className="rounded-lg border border-border bg-secondary/20 p-3">
                  <div className="text-xs text-muted-foreground">This month ({spend.this_month.month})</div>
                  <div className="text-2xl font-bold tracking-tight" data-testid="mailer-spend-month">${spend.this_month.cost.toFixed(2)}</div>
                  <div className="text-xs text-muted-foreground">{spend.this_month.count} live piece{spend.this_month.count === 1 ? "" : "s"} mailed</div>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">All-time live spend</span>
                  <span className="font-semibold" data-testid="mailer-spend-total">${spend.total.cost.toFixed(2)} · {spend.total.count} pcs</span>
                </div>
                <div className="text-[11px] text-muted-foreground border-t border-border pt-2">
                  Est. rates: letter ${spend.rates.letter.toFixed(2)} · postcard ${spend.rates.postcard.toFixed(2)} per piece. Test pieces aren't charged.
                </div>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">No live spend yet — test mailers are free.</div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-5" data-testid="mailer-history">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">Mailing history</h3>
              <button onClick={() => loadMailings(true)} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="mailer-history-refresh">
                {mailingsBusy && <Loader2 className="h-3 w-3 animate-spin" />}Refresh status
              </button>
            </div>
            {mailingsBusy ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading…</div>
            ) : mailings.length === 0 ? (
              <div className="text-xs text-muted-foreground" data-testid="mailer-history-empty">No mailers sent yet.</div>
            ) : (
              <ul className="space-y-2.5">
                {mailings.map((m) => {
                  const st = (m.status || "").toLowerCase();
                  const stCls = st === "cancelled" || st === "returned_to_sender" ? "text-destructive"
                    : st === "completed" ? "text-emerald-600"
                    : "text-primary";
                  return (
                    <li key={m.id} className="text-xs border-b border-border pb-2.5 last:border-0 last:pb-0" data-testid="mailer-history-item">
                      <div className="flex items-center justify-between">
                        <span className="capitalize font-medium">{m.type} · {m.live ? "LIVE" : "test"}</span>
                        <span className="flex items-center gap-2 text-muted-foreground">
                          {m.url && <a href={m.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">PDF</a>}
                          {m.created_at ? new Date(m.created_at).toLocaleDateString() : ""}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className={`inline-flex items-center gap-1 font-medium ${stCls}`} data-testid="mailer-history-status">
                          {st ? st.replace(/_/g, " ") : "ready"}
                        </span>
                        <span className="text-muted-foreground" data-testid="mailer-history-cost">
                          {m.live ? (st === "cancelled" ? "$0.00 (not charged)" : `~$${Number(m.cost ?? 0).toFixed(2)}`) : "test · free"}
                        </span>
                      </div>
                      {m.cancel_note && (
                        <div className="mt-1 text-[11px] text-destructive/90 bg-destructive/5 border border-destructive/20 rounded px-2 py-1" data-testid="mailer-history-cancel">
                          {m.cancel_note}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl" data-testid="mailer-preview-dialog">
          <DialogHeader>
            <DialogTitle>{(previewData || []).length > 1 ? "Mailer preview" : (previewData?.[0]?.type === "postcard" ? "Postcard preview" : "Letter preview")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 max-h-[72vh] overflow-y-auto">
            {(previewData || []).map((pd, idx) => (
              pd.type === "postcard" ? (
                <div key={idx} className="space-y-3">
                  {(previewData || []).length > 1 && <div className="text-sm font-semibold">Postcard</div>}
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Front</div>
                    <iframe title="postcard-front" srcDoc={pd.front_html} className="w-full rounded-md border border-border bg-white" style={{ aspectRatio: "6 / 4" }} data-testid="mailer-preview-front" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Back</div>
                    <iframe title="postcard-back" srcDoc={pd.back_html} className="w-full rounded-md border border-border bg-white" style={{ aspectRatio: "6 / 4" }} data-testid="mailer-preview-back" />
                  </div>
                </div>
              ) : (
                <div key={idx} className="space-y-2">
                  {(previewData || []).length > 1 && <div className="text-sm font-semibold">Letter</div>}
                  <iframe title="letter" srcDoc={pd.letter_html || ""} className="w-full rounded-md border border-border bg-white" style={{ height: "70vh" }} data-testid="mailer-preview-letter" />
                </div>
              )
            ))}
          </div>
          <p className="text-xs text-muted-foreground">This is exactly what Arqitek will send to PostGrid to print. Test mode won't charge or mail.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
